import { Component, computed, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { Upload, UpdateUpload, NewUpload, UploadTag } from '@xmedical/core';
import { XmedicalUiModule } from '@xmedical/ui';
import { WithReferralInfo, hasReferralInfo, formatReferralSource } from '../../types/referral-info';

/**
 * Extended Upload interface that includes referral info.
 * The base Upload from xmedical-core will have these properties added from the API.
 */
export type UploadWithReferralInfo = Upload & WithReferralInfo;

/**
 * Event emitted when a file upload is completed.
 */
export interface UploadAddedEvent {
  file: File;
  fileName: string;
  extension: string;
  size: number;
  path: string;
  tags: UploadTag[];
}

/**
 * Event emitted when an upload is deleted.
 */
export interface UploadDeletedEvent {
  upload: Upload;
}

/**
 * Event emitted when an upload is updated (renamed or tags changed).
 */
export interface UploadUpdatedEvent {
  upload: UpdateUpload;
}

/**
 * Event emitted when a download is requested.
 */
export interface UploadDownloadRequestedEvent {
  upload: Upload;
}

@Component({
  selector: 'xm-uploads-widget-view',
  templateUrl: './uploads-widget-view.component.html',
  styleUrls: ['./uploads-widget-view.component.scss'],
  imports: [
    DatePipe,
    FormsModule,
    MatButtonModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTableModule,
    XmedicalUiModule
  ]
})
export class UploadsWidgetViewComponent {
  // Inputs
  uploads = input<Upload[]>([]);
  title = input<string>('Uploads');
  isLoading = input<boolean>(false);
  isProcessing = input<boolean>(false);
  canUpload = input<boolean>(true);
  canDelete = input<boolean>(true);
  canEdit = input<boolean>(true);

  // Outputs
  uploadAdded = output<UploadAddedEvent>();
  uploadDeleted = output<UploadDeletedEvent>();
  uploadUpdated = output<UploadUpdatedEvent>();
  downloadRequested = output<UploadDownloadRequestedEvent>();

  // State for file editing
  editingUploadId: number | null = null;
  editFileName = '';
  editTags: UploadTag[] = [];

  // Drag and drop state
  isDragging = false;

  // Computed signals
  hasUploads = computed(() => this.uploads().length > 0);
  sortedUploads = computed(() =>
    [...this.uploads()].sort((a, b) => {
      const dateDiff = new Date(b.documentDate).getTime() - new Date(a.documentDate).getTime();
      if (dateDiff !== 0) return dateDiff;
      return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
    })
  );

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(Array.from(files));
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(Array.from(input.files));
      input.value = ''; // Reset input
    }
  }

  private handleFiles(files: File[]): void {
    for (const file of files) {
      const extension = file.name.split('.').pop() || '';
      const timestamp = Date.now();
      const uniquePath = `upload_${timestamp}_${file.name}`;

      this.uploadAdded.emit({
        file,
        fileName: file.name,
        extension,
        size: file.size,
        path: uniquePath,
        tags: [{ tag: 'patient', hexColor: '#2196F3' }]
      });
    }
  }

  onDownload(upload: Upload): void {
    this.downloadRequested.emit({ upload });
  }

  onDelete(upload: Upload): void {
    this.uploadDeleted.emit({ upload });
  }

  startEdit(upload: Upload): void {
    this.editingUploadId = upload.id;
    this.editFileName = upload.fileName;
    this.editTags = [...upload.tags];
  }

  cancelEdit(): void {
    this.editingUploadId = null;
    this.editFileName = '';
    this.editTags = [];
  }

  saveEdit(upload: Upload): void {
    if (this.editFileName.trim()) {
      this.uploadUpdated.emit({
        upload: {
          id: upload.id,
          fileName: this.editFileName.trim(),
          tags: this.editTags,
          documentDate: upload.documentDate
        }
      });
    }
    this.cancelEdit();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Referral helper methods for template
  isReferred(upload: UploadWithReferralInfo): boolean {
    return hasReferralInfo(upload);
  }

  getReferralText(upload: UploadWithReferralInfo): string {
    return formatReferralSource(upload);
  }
}
